import {
  ActionError,
  AttentionBlock,
  Button,
  ConfirmDialog,
  CopyField,
  EmptyState,
  Skeleton,
  StatusTag,
  Surface,
  formatDate,
  formatRelative,
  formatTime,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { LoadError } from '@/app/load-error'
import { errorMessage, fieldErrors } from '@/lib/errors'
import {
  useCreateWebhook,
  useDeleteWebhook,
  useRevealWebhookSecret,
  useRotateWebhookSecret,
  useTestWebhook,
  useUpdateWebhook,
  useWebhooks,
  type Integration,
  type Webhook,
  type WebhookSecret,
} from './api'
import { DOCS_WEBHOOKS_URL, eventsSummary, urlHost, webhookStatus } from './labels'
import { WebhookForm } from './webhook-form'

/**
 * Aba Webhooks (RN-INT-16 a 20): até 5 endereços por app. Cada um é um cartão: o endereço, os eventos, o status e
 * as ações em texto. O segredo sai uma vez ao criar e ao trocar, e depois só por "Revelar segredo", que fica na
 * trilha.
 */

const MAX_WEBHOOKS = 5
const noteClass = 'font-body text-secondary text-[12px] leading-[1.6] font-light'
const linkClass = 'text-ink border-hairline-heavy hover:border-ink border-b'

/** O segredo que acabou de aparecer (criar, trocar ou revelar), com o de antes nas 24 horas da troca. */
function SecretBlock({
  title,
  secret,
  previous,
  onHide,
}: {
  title: string
  secret: string | null
  previous?: { secret: string; validUntil: string } | null
  onHide: () => void
}) {
  return (
    <AttentionBlock title={title} aria-label="Segredo do webhook">
      {secret ? (
        <>
          <p>
            Com o segredo, o seu sistema confere que a entrega veio da Westhetics: o cabeçalho
            webhook-signature é o HMAC-SHA256 de &ldquo;id.timestamp.corpo&rdquo;, no padrão
            Standard Webhooks. Guarde num cofre; não mande por e-mail.
          </p>
          <CopyField
            className="mt-4"
            label="Segredo"
            value={secret}
            emphasis
            copyAriaLabel="Copiar o segredo do webhook"
            onCopied={() => toast('Segredo copiado')}
          />
          {previous ? (
            <CopyField
              className="mt-4"
              label="Segredo anterior"
              value={previous.secret}
              copyAriaLabel="Copiar o segredo anterior"
              note={`Continua assinando as entregas até ${formatDate(new Date(previous.validUntil))} às ${formatTime(new Date(previous.validUntil))}, junto com o novo.`}
            />
          ) : null}
        </>
      ) : (
        <p>O segredo já foi mostrado. Use "Revelar segredo" para ver de novo.</p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <a href={DOCS_WEBHOOKS_URL} target="_blank" rel="noreferrer" className={linkClass}>
          Como conferir a assinatura
        </a>
        <Button variant="text" onClick={onHide}>
          Esconder o segredo
        </Button>
      </div>
    </AttentionBlock>
  )
}

function WebhookCard({ integration, webhook }: { integration: Integration; webhook: Webhook }) {
  const update = useUpdateWebhook(integration.id)
  const remove = useDeleteWebhook(integration.id)
  const reveal = useRevealWebhookSecret()
  const rotate = useRotateWebhookSecret(integration.id)
  const test = useTestWebhook(integration.id)
  const status = webhookStatus(webhook)
  const host = urlHost(webhook.url)
  const isActive = webhook.status === 'active'
  // App revogado: o webhook fica só para consulta, como as permissões (RN-ADM-42).
  const isAppActive = integration.status === 'active'

  const [editing, setEditing] = useState(false)
  const [shown, setShown] = useState<{
    title: string
    secret: WebhookSecret | { secret: string | null }
  } | null>(null)
  const [confirming, setConfirming] = useState<'rotate' | 'delete' | null>(null)
  const [failure, setFailure] = useState<{ title: string; description: string } | null>(null)

  const fail = (title: string) => (error: unknown) =>
    setFailure({ title, description: errorMessage(error) })

  function setStatus(next: 'active' | 'disabled') {
    setFailure(null)
    update.mutate(
      { id: webhook.id, change: { status: next } },
      {
        onSuccess: () =>
          toast(
            next === 'active'
              ? `Webhook de ${host} ativado; os próximos eventos vão para ele`
              : `Webhook de ${host} desativado; os eventos de agora em diante não vão para ele`,
          ),
        onError: fail(next === 'active' ? 'Não foi possível ativar' : 'Não foi possível desativar'),
      },
    )
  }

  function sendTest() {
    setFailure(null)
    test.mutate(webhook.id, {
      onSuccess: () =>
        toast(`Teste na fila para ${host}; sai no próximo minuto. Veja em Entregas.`),
      onError: fail('Não foi possível enviar o teste'),
    })
  }

  function revealSecret() {
    setFailure(null)
    reveal.mutate(webhook.id, {
      onSuccess: (secret) => setShown({ title: `Segredo do webhook de ${host}`, secret }),
      onError: fail('Não foi possível revelar o segredo'),
    })
  }

  function confirmRotate() {
    rotate.mutate(webhook.id, {
      onSuccess: (result) => {
        setConfirming(null)
        setShown({ title: 'Segredo novo: copie agora', secret: { secret: result.secret } })
        toast('Segredo trocado; o anterior vale por mais 24 horas')
      },
      onError: (error) => {
        setConfirming(null)
        fail('Não foi possível trocar o segredo')(error)
      },
    })
  }

  function confirmDelete() {
    remove.mutate(webhook.id, {
      onSuccess: () => {
        setConfirming(null)
        toast(`Webhook de ${host} removido`)
      },
      onError: (error) => {
        setConfirming(null)
        fail('Não foi possível remover o webhook')(error)
      },
    })
  }

  const shownSecret = shown?.secret
  const previous =
    shownSecret &&
    'previousSecret' in shownSecret &&
    shownSecret.previousSecret &&
    shownSecret.previousSecretValidUntil
      ? { secret: shownSecret.previousSecret, validUntil: shownSecret.previousSecretValidUntil }
      : null

  return (
    <Surface className="max-desk:px-4" aria-label={`Webhook de ${host}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-name text-ink leading-[1.4] font-normal break-all">
            {host}
          </h3>
          <p className={`${noteClass} break-all`}>{webhook.url}</p>
          <p className={`${noteClass} mt-1`}>
            {[
              webhook.description,
              eventsSummary(webhook.events),
              webhook.lastSuccessAt
                ? `última entrega com sucesso ${formatRelative(new Date(webhook.lastSuccessAt))}`
                : 'nenhuma entrega com sucesso ainda',
              webhook.deliveries.failed > 0
                ? `${webhook.deliveries.failed} ${webhook.deliveries.failed === 1 ? 'entrega falhou' : 'entregas falharam'}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <StatusTag level={status.level}>{status.label}</StatusTag>
      </div>

      {webhook.disabledReason === 'failing' ? (
        <AttentionBlock
          className="mt-4"
          tone="pampas"
          title="Desligado depois de 72 horas só de falhas"
        >
          A Westhetics parou de tentar em{' '}
          {webhook.disabledAt ? formatDate(new Date(webhook.disabledAt)) : 'data não registrada'}.
          Corrija o endereço e ative; as entregas que falharam podem ser reenviadas em Entregas.
        </AttentionBlock>
      ) : webhook.failingSince && isActive ? (
        <AttentionBlock className="mt-4" tone="pampas" title="As entregas estão falhando">
          Desde {formatDate(new Date(webhook.failingSince))}, o endereço não responde 2xx. Se
          continuar assim por 72 horas, o webhook é desligado e as gestoras recebem o aviso.
        </AttentionBlock>
      ) : null}

      {failure ? (
        <ActionError
          className="mt-4"
          title={failure.title}
          description={failure.description}
          onDismiss={() => setFailure(null)}
        />
      ) : null}

      {shown ? (
        <div className="mt-4">
          <SecretBlock
            title={shown.title}
            secret={shown.secret.secret}
            previous={previous}
            onHide={() => setShown(null)}
          />
        </div>
      ) : null}

      {editing ? (
        <div className="border-hairline mt-4 border-t pt-4">
          <WebhookForm
            integration={integration}
            initial={webhook}
            submitLabel="Salvar o webhook"
            loadingLabel="Salvando…"
            loading={update.isPending}
            onSubmit={(input) =>
              update.mutateAsync({ id: webhook.id, change: input }).then(
                () => {
                  setEditing(false)
                  toast('Webhook salvo')
                },
                (error: unknown) => {
                  if (Object.keys(fieldErrors(error)).length === 0)
                    fail('Não foi possível salvar o webhook')(error)
                  throw error
                },
              )
            }
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : !isAppActive ? (
        <p className={`${noteClass} border-hairline mt-4 border-t pt-4`}>
          O app foi revogado: este webhook não recebe mais eventos e fica aqui só para consulta.
        </p>
      ) : (
        <div className="border-hairline mt-4 flex flex-wrap items-center gap-x-5 gap-y-3 border-t pt-4">
          <Button
            variant="text"
            onClick={sendTest}
            loading={test.isPending}
            loadingLabel="Enviando o teste…"
            disabled={!isActive}
          >
            {isActive ? 'Enviar teste' : 'Ative para testar'}
          </Button>
          <Button
            variant="text"
            onClick={revealSecret}
            loading={reveal.isPending}
            loadingLabel="Revelando…"
          >
            Revelar segredo
          </Button>
          <Button variant="text" onClick={() => setConfirming('rotate')}>
            Trocar o segredo
          </Button>
          <Button variant="text" onClick={() => setEditing(true)}>
            Alterar
          </Button>
          <Button
            variant="text"
            onClick={() => setStatus(isActive ? 'disabled' : 'active')}
            loading={update.isPending}
            loadingLabel={isActive ? 'Desativando…' : 'Ativando…'}
          >
            {isActive ? 'Desativar' : 'Ativar'}
          </Button>
          <Button variant="text" onClick={() => setConfirming('delete')}>
            Remover
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirming === 'rotate'}
        onOpenChange={(open) => setConfirming(open ? 'rotate' : null)}
        title={`Trocar o segredo do webhook de ${host}?`}
        effect="Sai um segredo novo, mostrado agora. O atual continua assinando as entregas por 24 horas, junto com o novo, para o seu sistema trocar sem perder evento. Os eventos e as entregas continuam."
        confirmLabel="Trocar o segredo"
        tone="primary"
        loading={rotate.isPending}
        loadingLabel="Trocando…"
        onConfirm={confirmRotate}
      />
      <ConfirmDialog
        open={confirming === 'delete'}
        onOpenChange={(open) => setConfirming(open ? 'delete' : null)}
        title={`Remover o webhook de ${host}?`}
        effect="As entregas na fila são canceladas e os próximos eventos não vão mais para este endereço. O histórico de entregas continua por 30 dias, e o app e a chave não mudam."
        alternative={
          isActive
            ? {
                description:
                  'Para pausar sem perder a configuração, desative: volta quando você ativar.',
                label: 'Desativar',
                onSelect: () => {
                  setConfirming(null)
                  setStatus('disabled')
                },
              }
            : undefined
        }
        confirmLabel="Remover o webhook"
        loading={remove.isPending}
        loadingLabel="Removendo…"
        onConfirm={confirmDelete}
      />
    </Surface>
  )
}

export function WebhooksTab({ integration }: { integration: Integration }) {
  const webhooks = useWebhooks(integration.id)
  const create = useCreateWebhook(integration.id)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<{ host: string; secret: string | null } | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const items = webhooks.data ?? []
  const isActive = integration.status === 'active'
  const atLimit = items.length >= MAX_WEBHOOKS

  const newButton = !isActive ? null : atLimit ? (
    <Button size="sm" disabled>
      Limite de {MAX_WEBHOOKS} webhooks
    </Button>
  ) : creating ? null : (
    <Button
      size="sm"
      variant="outline"
      onClick={() => {
        setCreated(null)
        setCreating(true)
      }}
    >
      + Novo webhook
    </Button>
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="font-body text-body text-secondary max-w-[640px] leading-[1.7] font-light">
          A cada evento da clínica que o app vê, a Westhetics faz um POST no endereço, assinado.
          Falhou, tenta de novo por até 42 horas.{' '}
          <a href={DOCS_WEBHOOKS_URL} target="_blank" rel="noreferrer" className={linkClass}>
            Os eventos e como receber
          </a>
          .
        </p>
        {newButton}
      </div>

      {failure ? (
        <ActionError
          title="Não foi possível criar o webhook"
          description={failure}
          onDismiss={() => setFailure(null)}
        />
      ) : null}

      {created ? (
        <SecretBlock
          title={`Webhook de ${created.host} criado: copie o segredo agora`}
          secret={created.secret}
          onHide={() => setCreated(null)}
        />
      ) : null}

      {creating ? (
        <Surface className="max-desk:px-4" aria-label="Novo webhook">
          <h3 className="font-display text-name text-ink mb-4 leading-[1.4] font-normal">
            Novo webhook
          </h3>
          <WebhookForm
            integration={integration}
            submitLabel="Criar webhook"
            loadingLabel="Criando…"
            loading={create.isPending}
            onSubmit={(input) =>
              create.mutateAsync(input).then(
                (result) => {
                  create.resetKey()
                  setCreating(false)
                  setFailure(null)
                  setCreated({ host: urlHost(result.webhook.url), secret: result.secret })
                },
                (error: unknown) => {
                  if (Object.keys(fieldErrors(error)).length === 0) setFailure(errorMessage(error))
                  throw error
                },
              )
            }
            onCancel={() => setCreating(false)}
          />
        </Surface>
      ) : null}

      {webhooks.isError ? (
        <LoadError
          title="Não conseguimos carregar os webhooks"
          error={webhooks.error}
          onRetry={() => void webhooks.refetch()}
        />
      ) : webhooks.isPending ? (
        <div role="status" aria-label="Carregando os webhooks" className="flex flex-col gap-3">
          <Skeleton className="h-[120px] w-full" />
        </div>
      ) : items.length === 0 && !creating ? (
        <EmptyState
          title="Nenhum webhook"
          description="Sem webhook, o sistema da clínica precisa perguntar à API o que mudou. Com ele, fica sabendo na hora: a paciente que respondeu a anamnese, o contrato que foi assinado."
          action={
            isActive ? (
              <Button variant="outline" onClick={() => setCreating(true)}>
                Criar o primeiro webhook
              </Button>
            ) : undefined
          }
        />
      ) : (
        items.map((webhook) => (
          <WebhookCard key={webhook.id} integration={integration} webhook={webhook} />
        ))
      )}
    </div>
  )
}
